<script setup lang="ts">
import type { CategoriesItem, SwordDrillItem, TriviaItem, WordScrambleItem } from '~/stores/challengeWheel'

import { MINI_GAME_CONFIG } from '~/stores/challengeWheel'

const store = useChallengeWheelStore()
const { data: ui } = useChallengeWheelUI()

const show = computed(() => store.phase === 'revealing' || store.phase === 'playing')

// ── Mini-game content helpers ───────────────────────────

const asSwordDrill = computed(
  () => store.currentChallenge?.content as SwordDrillItem | undefined,
)
const asTrivia = computed(
  () => store.currentChallenge?.content as TriviaItem | undefined,
)
const asCategories = computed(
  () => store.currentChallenge?.content as CategoriesItem | undefined,
)
const asScramble = computed(
  () => store.currentChallenge?.content as undefined | WordScrambleItem,
)

// Answer reveal for trivia / scramble / sword-drill
const answerRevealed = ref(false)
const scrambleAnswerRevealed = ref(false)
const swordDrillRevealed = ref(false)

watch(() => store.phase, phase => {
  if (phase !== 'playing') {
    answerRevealed.value = false
    scrambleAnswerRevealed.value = false
    swordDrillRevealed.value = false
  }
})

// When the Time's Up overlay clears, auto-reveal answers
watch(() => store.timesUp, (val, old) => {
  if (old && !val && store.phase === 'playing') {
    answerRevealed.value = true
    scrambleAnswerRevealed.value = true
    swordDrillRevealed.value = true
  }
})

// Show scramble hint only after 15 s have elapsed
const showScrambleHint = computed(() => {
  const elapsed = store.currentMiniGameDuration - store.timerSeconds
  return elapsed >= 15
})

// Scramble: shuffle the letters of the word
const scrambledWord = computed(() => {
  const word = asScramble.value?.word
  if (!word) {
    return ''
  }
  const letters = word.toUpperCase().split('')
  for (let i = letters.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const tmp = letters[i]!
    letters[i] = letters[j]!
    letters[j] = tmp
  }
  return letters.join('')
})

const gameConfig = computed(() => {
  if (!store.currentChallenge) {
    return null
  }
  return MINI_GAME_CONFIG[store.currentChallenge.game]
})

const gameLabel = computed(() =>
  ui.value?.setup.miniGames.games.find(g => g.value === store.currentChallenge?.game)?.label ?? '',
)
const gameColor = computed(() => gameConfig.value?.color ?? '#6b7280')
</script>

<template>
  <Transition name="modal">
    <div
      v-if="show"
      class="absolute inset-0 z-20 flex flex-col items-center justify-center bg-slate-950/95 px-8 text-center"
    >
      <!-- ── Revealing phase: category banner ── -->
      <template v-if="store.phase === 'revealing'">
        <div class="flex flex-col items-center gap-[3vw]">
          <!-- Color pill -->
          <div
            :style="{ backgroundColor: gameColor }"
            class="rounded-full px-[4vw] py-[1.5vw] shadow-2xl ring-4 ring-white/20"
          >
            <span class="font-black uppercase tracking-widest text-white" style="font-size: clamp(2rem, 5vw, 8rem);">
              {{ gameLabel }}
            </span>
          </div>
          <!-- Countdown hero number — :key restarts animation on each tick -->
          <span
            :key="store.revealCountdown"
            class="animate-count-slam select-none font-black leading-none text-white drop-shadow-2xl"
            style="font-size: clamp(8rem, 22vw, 36rem);"
          >{{ store.revealCountdown }}</span>
        </div>
      </template>

      <!-- ── Playing phase: mini-game content ── -->
      <template v-else-if="store.phase === 'playing' && store.currentChallenge">
        <div class="flex w-full max-w-[90%] flex-col items-center gap-[3vw]">
          <!-- Game label badge -->
          <div
            :style="{ backgroundColor: gameColor }"
            class="rounded-full px-[3vw] py-[0.75vw] shadow-lg"
          >
            <span class="font-black uppercase tracking-widest text-white" style="font-size: clamp(1rem, 2vw, 3.5rem);">
              {{ gameLabel }}
            </span>
          </div>

          <!-- Sword Drill -->
          <template v-if="store.currentChallenge.game === 'sword-drill'">
            <p class="font-black text-amber-300 drop-shadow-lg" style="font-size: clamp(2.5rem, 6vw, 10rem);">
              {{ asSwordDrill?.reference }}
            </p>
            <Transition name="fade" mode="out-in">
              <div
                v-if="swordDrillRevealed"
                class="rounded-2xl bg-amber-600/20 px-[3vw] py-[1.5vw] ring-2 ring-amber-400/60"
              >
                <p class="leading-relaxed text-white" style="font-size: clamp(1.2rem, 2.8vw, 5rem);">
                  "{{ asSwordDrill?.text }}"
                </p>
              </div>
              <button
                v-else
                class="rounded-2xl bg-white/10 px-[3vw] py-[1.5vw] font-black text-white ring-2 ring-white/30 transition hover:bg-white/20"
                style="font-size: clamp(1.2rem, 3vw, 5rem);"
                @click="swordDrillRevealed = true; store.stopTimer()"
              >
                {{ ui?.modal.revealAnswer }}
              </button>
            </Transition>
          </template>

          <!-- Bible Trivia -->
          <template v-else-if="store.currentChallenge.game === 'bible-trivia'">
            <p class="font-bold leading-snug text-white" style="font-size: clamp(1.5rem, 3.5vw, 6rem);">
              {{ asTrivia?.question }}
            </p>
            <Transition name="fade" mode="out-in">
              <div
                v-if="answerRevealed"
                class="rounded-2xl bg-blue-600/30 px-[3vw] py-[1.5vw] ring-2 ring-blue-400"
              >
                <p class="font-bold text-blue-200" style="font-size: clamp(1.2rem, 3vw, 5rem);">
                  {{ asTrivia?.answer }}
                </p>
              </div>
              <button
                v-else
                class="rounded-2xl bg-white/10 px-[3vw] py-[1.5vw] font-black text-white ring-2 ring-white/30 transition hover:bg-white/20"
                style="font-size: clamp(1.2rem, 3vw, 5rem);"
                @click="answerRevealed = true; store.stopTimer()"
              >
                {{ ui?.modal.revealAnswer }}
              </button>
            </Transition>
          </template>

          <!-- Categories -->
          <template v-else-if="store.currentChallenge.game === 'categories'">
            <p class="font-black uppercase tracking-wide text-emerald-300 drop-shadow-lg" style="font-size: clamp(2rem, 6vw, 10rem);">
              {{ asCategories?.category }}
            </p>
          </template>

          <!-- Word Scramble -->
          <template v-else-if="store.currentChallenge.game === 'word-scramble'">
            <div class="flex flex-wrap justify-center gap-[1vw]">
              <span
                v-for="(letter, li) in scrambledWord.split('')"
                :key="li"
                class="flex items-center justify-center rounded-xl bg-violet-600 font-black text-white shadow-lg ring-2 ring-violet-300/40"
                style="width: clamp(2.5rem, 5vw, 8rem); height: clamp(2.5rem, 5vw, 8rem); font-size: clamp(1.2rem, 3vw, 5rem);"
              >{{ letter }}</span>
            </div>
            <p
              v-if="asScramble?.hint && showScrambleHint"
              class="font-semibold text-white/80"
              style="font-size: clamp(1.2rem, 2.8vw, 4.5rem);"
            >
              {{ ui?.modal.hintPrefix }} {{ asScramble.hint }}
            </p>
            <div class="flex w-full items-center justify-center" style="height: clamp(5rem, 9vw, 14rem);">
              <Transition name="fade" mode="out-in">
                <p
                  v-if="scrambleAnswerRevealed"
                  class="font-black tracking-widest text-violet-300 drop-shadow-lg"
                  style="font-size: clamp(2rem, 5vw, 8rem);"
                >
                  {{ asScramble?.word?.toUpperCase() }}
                </p>
                <button
                  v-else
                  class="rounded-2xl bg-white/10 px-[3vw] py-[1.5vw] font-black text-white ring-2 ring-white/30 transition hover:bg-white/20"
                  style="font-size: clamp(1.2rem, 3vw, 5rem);"
                  @click="scrambleAnswerRevealed = true; store.stopTimer()"
                >
                  {{ ui?.modal.revealAnswer }}
                </button>
              </Transition>
            </div>
          </template>

          <!-- Dismiss button (no points awarded yet) -->
          <button
            v-if="!store.roundPointAwarded"
            class="mt-[1vw] flex items-center justify-center rounded-full bg-white/10 text-white/60 ring-2 ring-white/20 transition hover:bg-white/20 hover:text-white"
            style="width: clamp(2.5rem, 4vw, 6rem); height: clamp(2.5rem, 4vw, 6rem); font-size: clamp(1.2rem, 2.5vw, 4rem);"
            @click="store.dismissRound()"
          >
            ✕
          </button>
        </div>
      </template>
      <!-- Time's Up super-overlay — covers the playing content until it auto-fades -->
      <Transition name="timesup">
        <div
          v-if="store.timesUp"
          class="absolute inset-0 flex items-center justify-center bg-slate-950/95"
        >
          <span class="font-black text-red-400 drop-shadow-2xl" style="font-size: clamp(2.5rem, 7vw, 12rem);">
            Time's Up!
          </span>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active {
  transition: opacity 0.4s ease;
}
.modal-leave-active {
  transition: opacity 0.5s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.timesup-enter-active {
  transition: opacity 0.3s ease;
}
.timesup-leave-active {
  transition: opacity 1.5s ease;
}
.timesup-enter-from,
.timesup-leave-to {
  opacity: 0;
}

/* Countdown hero: slams in large → settles at normal size */
@keyframes count-slam {
  0%   { opacity: 0; transform: scale(2.2); }
  40%  { opacity: 1; transform: scale(0.92); }
  65%  { transform: scale(1.05); }
  100% { opacity: 1; transform: scale(1); }
}
.animate-count-slam {
  display: inline-block;
  animation: count-slam 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes bounce-slow {
  0%, 100% { transform: translateY(-8px); }
  50% { transform: translateY(0); }
}
.animate-bounce-slow {
  animation: bounce-slow 1.2s ease-in-out infinite;
}
</style>
